import React, { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import * as dateFns from "date-fns";

// region Imports - Languages
import useTranslation from "src/translations/useTranslation";
import {
  DataTableMessages,
  GlobalMessages,
  OdometerHourmeterReportMessages,
  TravelHistoryMessages
} from "@shared/languages/interfaces";
// endregion Imports - Languages

// region Imports - Interfaces
import {
  TotalOdometerHourmeterSynthetic,
  TotalOdometerHourmeterAnalytic
} from "@shared/interfaces/total-odometer-hourmeter-report.interface";
// endregion Imports - Interfaces

// region Imports - Shared
import { ReportTypes } from "@shared/constants/report-types.enum";
import { formatOdometer, formatHourMeter } from "@shared/utils/useful-functions";
// endregion Imports - Shared

// region Imports - Material-UI
import { TextField } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
// endregion Imports - Material-UI

// region Imports - Utils
import utils from "@utils/useful-functions";
// endregion Imports - Utils

// region Imports - Components
import Header from "@components/Header";
import NewDataTable, { DataTableColumns, DataTableButtons } from "@molecules/NewDataTable";
import SegmentedControl from "@atoms/SegmentedControl";
import QueryFilterOdometerHourmeterReport from "@components/organisms/QueryFilterOdometerHourmeterReport";
// endregion Imports - Components

// region Imports - Styles
import { ScreenPlatform } from "@store/ducks/Screen/screen.type";
import { Container } from "./styles";
// endregion Imports - Styles

type ReportData = TotalOdometerHourmeterSynthetic | TotalOdometerHourmeterAnalytic;

interface VehicleOption {
  code: string;
  label: string;
}

const OdometerHourmeterReport: React.FC = () => {

  // region States
  const [reportData, setReportData] = useState<ReportData[]>([]);
  const [openFilterFields, setOpenFilterFields] = useState(true);
  const [numberOfFilteredOptions, setNumberOfFilteredOptions] = useState(0);
  const [openScheduleReportModal, setOpenScheduleReportModal] = useState(false);
  const [reportType, setReportType] = useState<ReportTypes>(ReportTypes.SYNTHETIC);
  const [openVehicleFilter, setOpenVehicleFilter] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleOption | null>(null);

  const [startDate, setStartDate] = useState<string>(
    dateFns.format(dateFns.subDays(new Date(), 1), "yyyy-MM-dd").concat("T00:00")
  );
  const [finishDate, setFinishDate] = useState<string>(
    dateFns.format(new Date(), "yyyy-MM-dd").concat("T23:59")
  );
  // endregion States

  // region Hooks
  const { screen } = useSelector((screen) => screen);
  const { t } = useTranslation();
  // endregion Hooks

  // region Memos
  const availableVehicles = useMemo(() => {
    if (reportType !== ReportTypes.ANALYTIC || reportData.length === 0) return [];

    const vehicleSet = new Map<string, string>();

    (reportData as TotalOdometerHourmeterAnalytic[]).forEach((item) => {
      if (!vehicleSet.has(item.vehicleCode)) {
        vehicleSet.set(item.vehicleCode, `${item.vehicleCode} - ${item.vehicleDescription}`);
      }
    });

    return Array.from(vehicleSet.entries()).map(([code, label]) => ({ code, label }));
  }, [reportData, reportType]);

  const filteredAnalyticData = useMemo(() => {
    if (reportType !== ReportTypes.ANALYTIC) return reportData;
    if (!selectedVehicle) return reportData;

    return (reportData as TotalOdometerHourmeterAnalytic[]).filter(
      (item) => item.vehicleCode === selectedVehicle.code
    );
  }, [reportData, reportType, selectedVehicle]);

  const displayData = reportType === ReportTypes.SYNTHETIC ? reportData : filteredAnalyticData;
  // endregion Memos

  // region Constants
  const dataTableSettings: DataTables.Settings = {
    order: [[0, "asc"]],
    columnDefs: [{ className: "dt-center", targets: -1 }]
  };

  const syntheticColumns: DataTableColumns[] = [
    {
      title: t(OdometerHourmeterReportMessages.vehicleCode),
      data: (d: TotalOdometerHourmeterSynthetic) => d.vehicleCode || "-",
      propertyName: "vehicleCode",
      filterable: false
    },
    {
      title: t(OdometerHourmeterReportMessages.vehicleDescription),
      data: (d: TotalOdometerHourmeterSynthetic) => d.vehicleDescription || "-",
      propertyName: "vehicleDescription",
      filterable: false
    },
    {
      title: t(OdometerHourmeterReportMessages.originalAllocation),
      data: (d: TotalOdometerHourmeterSynthetic) => d.originalAllocation || "-",
      propertyName: "originalAllocation",
      filterable: false
    },
    {
      title: t(OdometerHourmeterReportMessages.currentAllocation),
      data: (d: TotalOdometerHourmeterSynthetic) => d.currentAllocation || "-",
      propertyName: "currentAllocation",
      filterable: false
    },
    {
      title: t(OdometerHourmeterReportMessages.startDateTime),
      data: (d: TotalOdometerHourmeterSynthetic) => d.startDateTime ? dateFns.format(new Date(d.startDateTime), "dd/MM/yyyy") : "-",
      propertyName: "startDateTime",
      filterable: false
    },
    {
      title: t(OdometerHourmeterReportMessages.endDateTime),
      data: (d: TotalOdometerHourmeterSynthetic) => d.endDateTime ? dateFns.format(new Date(d.endDateTime), "dd/MM/yyyy") : "-",
      propertyName: "endDateTime",
      filterable: false
    },
    {
      title: t(OdometerHourmeterReportMessages.totalOdometer),
      data: (d: TotalOdometerHourmeterSynthetic) => formatOdometer(d.totalOdometer ?? 0, false),
      propertyName: "totalOdometer",
      filterable: false
    },
    {
      title: t(OdometerHourmeterReportMessages.totalHourmeter),
      data: (d: TotalOdometerHourmeterSynthetic) => formatHourMeter(d.totalHourmeter ?? 0),
      propertyName: "totalHourmeter",
      filterable: false
    }
  ];

  const analyticColumns: DataTableColumns[] = [
    {
      title: t(OdometerHourmeterReportMessages.vehicleCode),
      data: (d: TotalOdometerHourmeterAnalytic) => d.vehicleCode || "-",
      propertyName: "vehicleCode",
      filterable: false,
      visible: false
    },
    {
      title: t(OdometerHourmeterReportMessages.date),
      data: (d: TotalOdometerHourmeterAnalytic) => (d.date ? dateFns.format(dateFns.parse(d.date, "yyyy-MM-dd", new Date()), "dd/MM/yyyy") : "-"),
      propertyName: "date",
      filterable: false
    },
    {
      title: t(OdometerHourmeterReportMessages.dailyOdometer),
      data: (d: TotalOdometerHourmeterAnalytic) => formatOdometer(d.dailyOdometer ?? 0, false),
      propertyName: "dailyOdometer",
      filterable: false
    },
    {
      title: t(OdometerHourmeterReportMessages.dailyHourmeter),
      data: (d: TotalOdometerHourmeterAnalytic) => formatHourMeter(d.dailyHourmeter ?? 0),
      propertyName: "dailyHourmeter",
      filterable: false
    }
  ];

  const currentColumns = reportType === ReportTypes.SYNTHETIC ? syntheticColumns : analyticColumns;

  const dataTableButtons: DataTableButtons[] = [
    {
      name: t(DataTableMessages.buttonsPrint),
      key: "print",
      callback: () => utils.clickButtonDomElement("button-print"),
      extend: "print",
      className: "button-print",
      exportOptions: { columns: ":visible" }
    },
    {
      name: t(DataTableMessages.buttonsExport),
      callback: () => utils.clickButtonDomElement("button-export"),
      extend: "csv",
      key: "export",
      fieldSeparator: ";",
      className: "button-export",
      filename: `relatorio_odometro_horimetro_${startDate}__${finishDate}`,
      exportOptions: { columns: ":visible" }
    }
  ];
  // endregion Constants

  // region Functions
  const handleApplyFilter = (
    numberOfFilteredOptions: number,
    returnedData: ReportData[],
    startDate: string,
    finishDate: string
  ) => {
    setNumberOfFilteredOptions(numberOfFilteredOptions);
    setReportData(returnedData);
    setStartDate(startDate);
    setFinishDate(finishDate);

    const first = returnedData[0] as TotalOdometerHourmeterAnalytic | undefined;

    setSelectedVehicle(first ? { code: first.vehicleCode, label: `${first.vehicleCode} - ${first.vehicleDescription}` } : null);
  };

  const handleChangeReportType = useCallback((value: string) => {
    setReportType(value as ReportTypes);
    setSelectedVehicle(null);
  }, []);

  // endregion Functions

  return (
    <>
      <Header title={t(OdometerHourmeterReportMessages.title)} />
      <Container platform={screen.platform as ScreenPlatform} className="page">
        <div className="report-controls">
          <SegmentedControl
            className="segmented-control"
            name="reportType"
            segments={[
              { value: ReportTypes.SYNTHETIC, label: t(TravelHistoryMessages.syntheticReport) },
              { value: ReportTypes.ANALYTIC, label: t(TravelHistoryMessages.analyticReport) }
            ]}
            defaultIndex={0}
            onChange={handleChangeReportType}
          />
        </div>
        {reportType === ReportTypes.ANALYTIC && availableVehicles.length > 0 && (
          <div className="vehicle-filter-section">
            <div className="vehicle-filter-header">
              <span className="vehicle-filter-subtitle">{t(OdometerHourmeterReportMessages.selectVehicle)}</span>
            </div>
            <Autocomplete
              className="vehicle-selector"
              open={openVehicleFilter}
              onOpen={() => setOpenVehicleFilter(true)}
              onClose={() => setOpenVehicleFilter(false)}
              value={selectedVehicle}
              onChange={(_event, value) => setSelectedVehicle(value)}
              getOptionSelected={(option, value) => option.code === value.code}
              getOptionLabel={(option) => option.label}
              options={availableVehicles}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t(GlobalMessages.vehicle)}
                  variant="outlined"
                />
              )}
            />
          </div>
        )}
        <NewDataTable
          key={`${reportType}-${displayData.length}`}
          title={t(OdometerHourmeterReportMessages.title)}
          data={displayData}
          columns={currentColumns}
          settings={dataTableSettings}
          buttons={dataTableButtons}
          filters
          onClickFilterButton={() => setOpenFilterFields(true)}
          numberOfFilteredOptions={numberOfFilteredOptions}
        />
        <QueryFilterOdometerHourmeterReport
          open={openFilterFields}
          onClose={() => setOpenFilterFields(false)}
          onFilter={handleApplyFilter}
          reportType={reportType}
          generateScheduleReport={{
            openModal: openScheduleReportModal,
            onCloseModal: () => setOpenScheduleReportModal(false)
          }}
        />
      </Container>
    </>
  );
};

export default OdometerHourmeterReport;
